import { useState, useEffect } from "react";
import {
    Folder,
    File,
    FileText,
    FileImage,
    Film,
    Music,
    Archive,
    FileSpreadsheet,
    Code,
    ChevronRight,
    Home,
    ArrowLeft,
    Check,
    Package,
    X,
} from "lucide-react";
import { listFiles, createBundle } from "../../utils/api";
import { formatBytes } from "../../utils/fileHelpers";
import Modal from "../Modal/Modal";

import "./BundleCreator.css";

function getFileIcon(mimeType, name) {
    const ext = name ? name.split(".").pop().toLowerCase() : "";
    if (mimeType?.includes("pdf") || ext === "pdf")
        return <FileText size={16} />;
    if (
        mimeType?.includes("image") ||
        ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)
    )
        return <FileImage size={16} />;
    if (
        mimeType?.includes("video") ||
        ["mp4", "mov", "avi", "mkv"].includes(ext)
    )
        return <Film size={16} />;
    if (
        mimeType?.includes("audio") ||
        ["mp3", "wav", "flac", "aac"].includes(ext)
    )
        return <Music size={16} />;
    if (["zip", "rar", "7z", "tar", "gz"].includes(ext))
        return <Archive size={16} />;
    if (["doc", "docx"].includes(ext)) return <FileText size={16} />;
    if (["xls", "xlsx", "csv"].includes(ext))
        return <FileSpreadsheet size={16} />;
    if (
        ["js", "ts", "py", "java", "cpp", "c", "html", "css", "json"].includes(
            ext,
        )
    )
        return <Code size={16} />;
    return <File size={16} />;
}

export default function BundleCreator({ onClose, onCreated }) {
    const [stack, setStack] = useState([{ id: null, name: "My Files" }]);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState({}); // { fileId: fileObj }
    const [bundleName, setBundleName] = useState("");
    const [step, setStep] = useState("pick"); // "pick" | "name" | "done"
    const [bundleLink, setBundleLink] = useState("");
    const [creating, setCreating] = useState(false);

    const current = stack[stack.length - 1];

    useEffect(() => {
        setLoading(true);
        listFiles(current.id)
            .then((res) => setItems(res.data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [current.id]);

    const toggleFile = (item) => {
        setSelected((prev) => {
            const next = { ...prev };
            if (next[item._id]) delete next[item._id];
            else next[item._id] = item;
            return next;
        });
    };

    const navigateInto = (folder) => {
        setStack((prev) => [...prev, { id: folder._id, name: folder.name }]);
    };

    const navigateBack = () => {
        if (stack.length > 1) setStack((prev) => prev.slice(0, -1));
    };

    const navigateTo = (i) => setStack((prev) => prev.slice(0, i + 1));

    const selectedList = Object.values(selected);

    const handleCreate = async () => {
        if (selectedList.length === 0) return;
        setCreating(true);
        try {
            const res = await createBundle(
                bundleName.trim() || "Shared Files",
                selectedList.map((f) => f._id),
            );
            const link = `${window.location.origin}/bundle/${res.data.shareToken}`;
            setBundleLink(link);
            setStep("done");
        } catch (e) {
            console.log(e)
            alert("Failed to create bundle");
        } finally {
            setCreating(false);
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(bundleLink);
    };

    return (
        <Modal title="Create Share Bundle" onClose={onClose}>
            {step === "pick" && (
                <div className="bundle-creator">
                    <p className="bundle-hint">
                        Browse your files and select what you want to share.
                        Selected files will be accessible via a single link.
                    </p>

                    {/* Folder breadcrumb */}
                    <div className="fp-breadcrumbs" style={{ marginBottom: 8 }}>
                        {stack.map((crumb, i) => (
                            <span key={i} className="fp-crumb-wrap">
                                {i > 0 && (
                                    <ChevronRight
                                        size={13}
                                        className="fp-sep"
                                    />
                                )}
                                <button
                                    className={`fp-crumb ${i === stack.length - 1 ? "active" : ""}`}
                                    onClick={() => navigateTo(i)}
                                >
                                    {i === 0 ? <Home size={13} /> : null}
                                    {crumb.name}
                                </button>
                            </span>
                        ))}
                    </div>

                    {/* File/folder list */}
                    <div className="bundle-file-list">
                        {stack.length > 1 && (
                            <button
                                className="fp-item fp-back"
                                onClick={navigateBack}
                            >
                                <ArrowLeft size={16} /> Back
                            </button>
                        )}
                        {loading ? (
                            <div className="fp-empty">Loading…</div>
                        ) : items.length === 0 ? (
                            <div className="fp-empty">Empty folder</div>
                        ) : (
                            items.map((item) =>
                                item.type === "folder" ? (
                                    <button
                                        key={item._id}
                                        className="bundle-row bundle-folder"
                                        onClick={() => navigateInto(item)}
                                    >
                                        <Folder
                                            size={17}
                                            className="bundle-row-icon folder"
                                        />
                                        <span className="bundle-row-name">
                                            {item.name}
                                        </span>
                                        <ChevronRight
                                            size={15}
                                            style={{
                                                color: "var(--border-dk)",
                                                marginLeft: "auto",
                                            }}
                                        />
                                    </button>
                                ) : (
                                    <button
                                        key={item._id}
                                        className={`bundle-row bundle-file ${selected[item._id] ? "checked" : ""}`}
                                        onClick={() => toggleFile(item)}
                                    >
                                        <div
                                            className={`bundle-checkbox ${selected[item._id] ? "on" : ""}`}
                                        >
                                            {selected[item._id] && (
                                                <Check size={11} />
                                            )}
                                        </div>
                                        <span className="bundle-row-icon file">
                                            {getFileIcon(
                                                item.mimeType,
                                                item.name,
                                            )}
                                        </span>
                                        <span className="bundle-row-name">
                                            {item.name}
                                        </span>
                                        <span className="bundle-row-size">
                                            {formatBytes(item.size)}
                                        </span>
                                    </button>
                                ),
                            )
                        )}
                    </div>

                    {/* Selected count + next */}
                    <div className="bundle-footer">
                        <span className="bundle-count">
                            {selectedList.length > 0
                                ? `${selectedList.length} file${selectedList.length > 1 ? "s" : ""} selected`
                                : "No files selected"}
                        </span>
                        <div style={{ display: "flex", gap: 8 }}>
                            <button className="btn-secondary" onClick={onClose}>
                                Cancel
                            </button>
                            <button
                                className="btn-primary"
                                disabled={selectedList.length === 0}
                                onClick={() => setStep("name")}
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === "name" && (
                <div className="bundle-creator">
                    <p className="bundle-hint">
                        Give this bundle a name so the recipient knows what it
                        is. You're sharing{" "}
                        <strong>
                            {selectedList.length} file
                            {selectedList.length > 1 ? "s" : ""}
                        </strong>
                        .
                    </p>

                    <div className="bundle-selected-list">
                        {selectedList.map((f) => (
                            <div key={f._id} className="bundle-selected-item">
                                <span className="bundle-row-icon file">
                                    {getFileIcon(f.mimeType, f.name)}
                                </span>
                                <span className="bundle-row-name">
                                    {f.name}
                                </span>
                                <button
                                    className="bundle-remove"
                                    onClick={() => toggleFile(f)}
                                    title="Remove"
                                >
                                    <X size={13} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <input
                        className="modal-input"
                        placeholder="Bundle name (e.g. Q1 Reports, Contracts)"
                        value={bundleName}
                        onChange={(e) => setBundleName(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                        autoFocus
                    />

                    <div className="bundle-footer">
                        <button
                            className="btn-secondary"
                            onClick={() => setStep("pick")}
                        >
                            Back
                        </button>
                        <button
                            className="btn-primary"
                            onClick={handleCreate}
                            disabled={creating || selectedList.length === 0}
                        >
                            {creating ? "Creating…" : "Create & Copy Link"}
                        </button>
                    </div>
                </div>
            )}

            {step === "done" && (
                <div className="bundle-creator">
                    <div className="bundle-done">
                        <div className="bundle-done-icon">
                            <Package size={40} />
                        </div>
                        <h3>Bundle created!</h3>
                        <p>
                            Share this link with anyone you want to give access
                            to these {selectedList.length} files.
                        </p>
                    </div>
                    <div
                        className="share-link-box"
                        style={{ marginBottom: 16 }}
                    >
                        <input
                            readOnly
                            value={bundleLink}
                            className="share-link-input"
                        />
                        <button className="btn-copy" onClick={handleCopy}>
                            Copy
                        </button>
                    </div>
                    <div
                        className="bundle-footer"
                        style={{ justifyContent: "flex-end" }}
                    >
                        <button
                            className="btn-primary"
                            onClick={() => {
                                onCreated?.();
                                onClose();
                            }}
                        >
                            Done
                        </button>
                    </div>
                </div>
            )}
        </Modal>
    );
}